import { computed, ref } from 'vue'
import { ResumeTheme } from '@/types'
import useUser from '@/composables/useUser'

type ResumeFont = 'default' | 'classic'

export type ResumeMargin = 'default' | 'narrow' | 'wide'

export type ResumeLineHeight = 'default' | 'single' | 'spacious'

interface Palette {
  primaryColor: string
  backgroundColor: string
  backgroundColorDark: string
  fontColor: string
}

export const useStyledResume = () => {
  const resume = ref<any>(null)
  const { user } = useUser()
  const resumeTemplatesDomain = computed(
    () => user.value?.domain?.resume_templates
  )
  const resumeTemplates = computed(() => {
    const templatesDomain = resumeTemplatesDomain.value
    if (!templatesDomain) return resumeTemplatesDefault
    const resumeTemplates = [...resumeTemplatesDefault, ...customTemplates]
    const templatesDomainEnabled = resumeTemplates.filter(
      template => templatesDomain[template.value]
    )
    return templatesDomainEnabled.length
      ? templatesDomainEnabled
      : resumeTemplatesDefault
  })
  const setResume = (newResume: any) => {
    resume.value = newResume
  }
  const defaultTemplate = computed(() => user.value?.domain?.default_template || 'basic')

  const resumeTemplateNames = [
    'classic',
    'modernprofessional',
    'modernstudent',
    'basic',
    'basicalt',
    'baylorundergraduate',
    'baylorbsw',
    'kubusug',
    'kubusfin',
  ]

  const resumeThemeNames: ResumeTheme[] = [
    'default',
    'bluey',
    'plurple',
    'greenish',
  ]

  const resumeTemplateName = computed(() =>
    resumeTemplateNames.includes(resume.value?.meta?.template)
      ? resume.value?.meta?.template
      : defaultTemplate.value
  )

  const resumeThemeName = computed<ResumeTheme>(() =>
    resumeThemeNames.includes(resume.value?.meta?.theme)
      ? resume.value?.meta?.theme
      : 'default'
  )

  const fonts: Record<ResumeFont, { fontFamily: string }> = {
    default: {
      fontFamily: 'Open Sans, sans-serif',
    },
    classic: {
      fontFamily: 'Times New Roman, sans-serif',
    },
  }

  const margins: Record<ResumeMargin, string> = {
    default: '4em',
    narrow: '3em',
    wide: '6em',
  }

  const defaultTemplateMargin: Record<string, ResumeMargin> = {
    'classic': 'wide',
    'modernprofessional': 'wide',
    'modernstudent': 'wide',
    'basic': 'default',
    'basicalt': 'default',
    'baylorundergraduate': 'default',
    'baylorbsw': 'default',
    'kubusug': 'wide',
    'kubusfin': 'wide',
  }

  const lineHeights: Record<ResumeLineHeight, string> = {
    single: '1.2', // equivalent to 1.0 in Word
    default: '1.5', // equivalent to 1.15 in Word
    spacious: '1.75', // equivalent to 1.5 in Word
  }

  const palettes: Record<string, Palette> = {
    default: {
      primaryColor: '#2f2f2f',
      backgroundColor: '#f6f6f6',
      backgroundColorDark: '#e8e8e8',
      fontColor: '#333',
    },
    bluey: {
      primaryColor: '#304C89',
      backgroundColor: '#F0F3FA',
      backgroundColorDark: '#D2DBEF',
      fontColor: '#10192D',
    },
    plurple: {
      primaryColor: '#777BA6',
      backgroundColor: '#E6E7EF',
      backgroundColorDark: '#CECFDF',
      fontColor: '#03012C',
    },
    greenish: {
      primaryColor: '#6A8A84',
      backgroundColor: '#E8EDEC',
      backgroundColorDark: '#C5D3CF',
      fontColor: '#2C3A37',
    },
  }

  const colors = computed<Palette>(() => {
    return palettes[resumeThemeName.value]
  })

  const font = computed<{ fontFamily: string }>(() =>
    fonts[resume.value?.meta?.font] || fonts.default
  )

  const margin = computed<string>(() => {
    const marginKey: ResumeMargin = resume.value?.meta?.marginSize || defaultTemplateMargin[resumeTemplateName.value] || 'default'

    return margins[marginKey] || margins.default
  })

  const lineHeight = computed<string>(() =>
    lineHeights[resume.value?.meta?.lineHeight] || lineHeights.default
  )

  const lineHeightOptions: Array<{ value: ResumeLineHeight; displayTitle: string; displaySize: string }> = [
    {
      value: 'single',
      displayTitle: 'Single',
      displaySize: '1.0',
    },
    {
      value: 'default',
      displayTitle: 'Default',
      displaySize: '1.15',
    },
    {
      value: 'spacious',
      displayTitle: 'More space',
      displaySize: '1.5',
    },
  ]

  const marginOptions: Array<{ value: ResumeMargin; displayTitle: string; displaySize: string }> = [
    {
      value: 'narrow',
      displayTitle: 'Narrow',
      displaySize: '0.5"',
    },
    {
      value: 'default',
      displayTitle: 'Normal',
      displaySize: '0.75"',
    },
    {
      value: 'wide',
      displayTitle: 'Wide',
      displaySize: '1.0"',
    },
  ]

  const resumeTemplatesDefault = [
    {
      value: 'classic',
      name: 'Classic Professional',
    },
    {
      value: 'modernprofessional',
      name: 'Modern Professional',
    },
    {
      value: 'modernstudent',
      name: 'Modern Student',
    },
    {
      value: 'basic',
      name: 'Minimalist Expert',
    },
    {
      value: 'basicalt',
      name: 'Timeless Professional',
    },
  ]

  const customTemplates = [
    {
      value: 'baylorundergraduate',
      name: 'ECS Resume',
    },
    {
      value: 'baylorbsw',
      name: 'BSW Resume',
    },
    {
      value: 'kubusug',
      name: 'KU Business Undergraduate',
    },
    {
      value: 'kubusfin',
      name: 'KU Business and Finance',
    },
  ]

  return {
    setResume,
    resumeThemeNames,
    resumeTemplateNames,
    resumeTemplateName,
    resumeThemeName,
    resumeTemplates,
    colors,
    font,
    margin,
    lineHeight,
    defaultTemplate,
    lineHeightOptions,
    marginOptions,
  }
}
